import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditCard, Calendar, Shield, Copy, Eye } from 'lucide-react'
import { licenseAPI, planAPI, stripeAPI } from '../services/api'
import type { License, SubscriptionPlan } from '../types'
import toast from 'react-hot-toast'
import PaymentModal from '../components/PaymentModal'

const Licenses: React.FC = () => {
  const { t } = useTranslation()
  const [licenses, setLicenses] = useState<License[]>([])
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showLicenseKeys, setShowLicenseKeys] = useState<{ [key: number]: boolean }>({})
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null)

  useEffect(() => {
    fetchLicenses()
    fetchPlans()
  }, [])

  const fetchLicenses = async () => {
    try {
      const response = await licenseAPI.getMyLicenses()
      console.log('Licenses API response:', response.data)
      
      // Handle different response formats
      const data = response.data as any
      if (Array.isArray(data)) {
        setLicenses(data)
      } else if (data && data.licenses) {
        setLicenses(data.licenses)
      } else if (data && data.history) {
        setLicenses(data.history)
      } else {
        console.error('Unexpected API response format:', data)
        setLicenses([])
      }
    } catch (error) {
      console.error('Error fetching licenses:', error)
      toast.error('Błąd pobierania licencji')
      setLicenses([])
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPlans = async () => {
    try {
      const response = await planAPI.getPlans()
      setPlans(response.data)
    } catch (error) {
      console.error('Error fetching plans:', error)
      setPlans([])
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-900/20 text-green-400 border-green-900/50'
      case 'expired':
        return 'bg-red-900/20 text-red-400 border-red-900/50'
      case 'cancelled':
        return 'bg-gray-900/20 text-gray-400 border-gray-900/50'
      default:
        return 'bg-yellow-900/20 text-yellow-400 border-yellow-900/50'
    }
  }

  const formatDate = (timestamp: number | string) => {
    try {
      const date = new Date(Number(timestamp) || timestamp)
      return date.toLocaleDateString()
    } catch {
      return 'Nieprawidłowa data'
    }
  }

  const copyLicenseKey = (key: string) => {
    navigator.clipboard.writeText(key)
    toast.success('Klucz licencji skopiowany!')
  }

  const toggleLicenseKeyVisibility = (licenseId: number) => {
    setShowLicenseKeys(prev => ({
      ...prev,
      [licenseId]: !prev[licenseId]
    }))
  }

  const maskLicenseKey = (key: string) => {
    if (!key) return 'Brak klucza licencji'
    const parts = key.split('-')
    if (parts.length === 4) {
      return `${parts[0]}-****-****-${parts[3]}`
    }
    return '****-****-****-****'
  }

  const handleCancelLicense = async (licenseId: number) => {
    if (!confirm('Czy na pewno chcesz anulować tę licencję? Ta operacja anuluje subskrypcję w Stripe i nie może być cofnięta.')) {
      return
    }

    try {
      const response = await stripeAPI.cancelSubscription(licenseId)
      if (response.data.success) {
        toast.success('Subskrypcja została anulowana pomyślnie')
        fetchLicenses() // Odśwież listę licencji
      } else {
        toast.error(response.data.message || 'Błąd anulowania subskrypcji')
      }
    } catch (error) {
      console.error('Cancel subscription error:', error)
      toast.error('Nie udało się anulować subskrypcji')
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">{t('licenses.title')}</h1>
        <p className="text-gray-400">{t('licenses.subtitle')}</p>
      </div>

      {/* Current Licenses */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <CreditCard className="h-5 w-5 text-primary mr-2" />
          {t('licenses.myLicenses')}
        </h2>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="animate-pulse bg-dark-700 h-20 rounded"></div>
            ))}
          </div>
        ) : licenses.length === 0 ? (
          <p className="text-gray-400 text-center py-8">{t('licenses.noLicenses')}</p>
        ) : (
          <div className="space-y-4">
            {licenses.map((license) => (
              <div key={license.id} className="bg-dark-700 rounded-lg p-4 border border-dark-500">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Shield className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{license.plan_name || `Plan ${license.plan_id}`}</h3>
                      <div className="flex items-center space-x-2">
                        <p className="text-sm text-gray-400">
                          {showLicenseKeys[license.id] 
                            ? license.license_key || 'Brak klucza licencji'
                            : maskLicenseKey(license.license_key)
                          }
                        </p>
                        <button
                          onClick={() => toggleLicenseKeyVisibility(license.id)}
                          className="text-gray-400 hover:text-white p-1"
                          title={showLicenseKeys[license.id] ? 'Ukryj klucz' : 'Pokaż klucz'}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {license.license_key && (
                          <button
                            onClick={() => copyLicenseKey(license.license_key)}
                            className="text-gray-400 hover:text-white p-1"
                            title="Skopiuj klucz"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('licenses.devicesCount', { 
                          count: license.active_devices || 0, 
                          max: license.max_devices 
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusColor(license.status)}`}>
                      {t(`licenses.${license.status}`, { defaultValue: license.status })}
                    </span>
                    <p className="text-sm text-gray-400 mt-1 flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {t('licenses.validUntil')}: {formatDate(license.valid_to)}
                    </p>
                  </div>
                </div>
                
                {license.status === 'active' && (
                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => handleCancelLicense(license.id)}
                      className="px-3 py-1 text-sm bg-red-600/20 text-red-400 hover:bg-red-600/30 border border-red-600/50 rounded transition-colors"
                    >
                      {t('licenses.cancel')}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available Plans */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-4">{t('licenses.availablePlans')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div key={plan.id} className="bg-dark-700 rounded-lg p-4 border border-dark-500">
              <h3 className="font-semibold text-white mb-2">{plan.name}</h3>
              <div className="mb-4">
                <span className="text-2xl font-bold text-primary">{plan.price_monthly} PLN</span>
                <span className="text-gray-400">/miesiąc</span>
              </div>
              <ul className="text-sm text-gray-400 mb-4 space-y-1">
                <li>• Do {plan.max_devices} urządzeń</li>
                <li>• {plan.features}</li>
              </ul>
              <button 
                onClick={() => {
                  setSelectedPlan(plan)
                  setShowPaymentModal(true)
                }}
                className="w-full px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
              >
                {t('payment.buyNow')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedPlan && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          plan={selectedPlan}
          onSuccess={() => {
            setShowPaymentModal(false)
            fetchLicenses()
            toast.success('Płatność zakończona pomyślnie!')
          }}
        />
      )}
    </div>
  )
}

export default Licenses